import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Chip,
  TextField,
  Button,
  Divider,
  ButtonGroup,
} from "@mui/material";
import { ContentCopy, FiberManualRecord } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ColorsConstants,
  dashboardCardGreenTextColor,
  grayFontColor,
} from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";
import { StringField } from "../ConfigFields/StringField";

dayjs.extend(relativeTime);

const CURRENCIES = [
  { code: "INR", label: "INR — Indian Rupee" },
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "AED", label: "AED — UAE Dirham" },
  { code: "SGD", label: "SGD — Singapore Dollar" },
];

const PAYMENT_STATUS_VALUES = ["Success", "Failed", "Expired", "Refunded"];

const EVENT_STATUS_DOT_COLOR = {
  PROCESSED: dashboardCardGreenTextColor,
  FAILED: "#b50000",
  IGNORED: grayFontColor,
  PENDING: grayFontColor,
};

const maskAccountId = (accountId) => {
  if (!accountId || accountId.length <= 10) return accountId;
  return `${accountId.slice(0, 8)}...${accountId.slice(-4)}`;
};

const PaymentLinkStripeSettings = ({
  gatewayDetails,
  stripeConfig,
  onStripeConfigChange,
  lastWebhookEvent,
}) => {
  const [copied, setCopied] = useState(false);

  const accountId = gatewayDetails?.accountId;
  const mode = gatewayDetails?.mode === "live" ? "Live" : "Test";

  const currency = stripeConfig?.currency || "INR";
  const successMessage = stripeConfig?.success_message || "";
  const paymentStatusVariable = stripeConfig?.payment_status_variable || "payment_status";

  const webhookUrl = gatewayDetails?.webhookUrl || "";
  const webhookRegistered = !!gatewayDetails?.webhookRegistered;
  const webhookRegistrationError = gatewayDetails?.webhookRegistrationError || "";

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: ColorsConstants.primaryGray10,
        borderRadius: 2,
        p: 2,
        backgroundColor: "#f9f9f9",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          Stripe settings
        </Typography>
        {accountId && (
          <Chip
            label={`${maskAccountId(accountId)} (${mode})`}
            size="small"
            sx={{
              backgroundColor: "#E6F4EA",
              color: dashboardCardGreenTextColor,
              fontWeight: 600,
            }}
          />
        )}
      </Stack>

      <Stack spacing={2.5}>
        <Box>
          <Typography sx={fieldLabelSx}>Mode</Typography>
          <TextField
            fullWidth
            disabled
            value={`${mode} (from connected Stripe account)`}
            sx={{ "& .MuiInputBase-input.Mui-disabled": { color: grayFontColor } }}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Currency</Typography>
          <Select
            fullWidth
            value={currency}
            onChange={(e) => onStripeConfigChange("currency", e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
            Fallback only — if the order already has its own currency set, the checkout charges in that currency instead.
          </Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Amount</Typography>
          <ButtonGroup fullWidth>
            <Button
              variant="contained"
              sx={{
                backgroundColor: ColorsConstants.primaryBlue100,
                "&:hover": { backgroundColor: ColorsConstants.primaryBlue100 },
              }}
            >
              Dynamic (from flow)
            </Button>
            <Button variant="outlined" disabled>
              Fixed amount
            </Button>
          </ButtonGroup>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
            Fixed amount is coming soon — the link amount is always taken from the flow for now.
          </Typography>
        </Box>

        <StringField
          fieldKey="success_message"
          schema={{ description: "Shown to customer after payment succeeds" }}
          value={successMessage}
          required={false}
          onChange={(val) => onStripeConfigChange("success_message", val)}
        />

        <Divider />

        <Box>
          <Typography sx={fieldLabelSx}>Webhook Endpoint</Typography>
          <Stack direction="row" gap={1}>
            <TextField
              fullWidth
              disabled
              value={webhookUrl || "Reconnect Stripe on the Integrations page to register a webhook endpoint"}
              sx={{ "& .MuiInputBase-input.Mui-disabled": { color: grayFontColor } }}
            />
            <Button
              variant="outlined"
              onClick={handleCopyWebhookUrl}
              disabled={!webhookUrl}
              startIcon={<ContentCopy fontSize="small" />}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </Stack>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
            {webhookRegistered
              ? "Registered with Stripe for this account — for reference/support only, not user-editable."
              : "Webhook registration failed when this account was connected — payment status updates won't arrive until you reconnect on the Integrations page."}
          </Typography>
          {!webhookRegistered && webhookRegistrationError && (
            <Typography sx={{ ...typography.bodySmall, color: "#b50000", mt: 0.5 }}>
              Stripe said: {webhookRegistrationError}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Last Webhook Event</Typography>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              border: "1px solid",
              borderColor: ColorsConstants.secondaryGray,
              borderRadius: 1,
              px: 1.5,
              py: 1,
              backgroundColor: "#fff",
            }}
          >
            <FiberManualRecord
              sx={{
                fontSize: 10,
                color: lastWebhookEvent
                  ? EVENT_STATUS_DOT_COLOR[lastWebhookEvent.processing_status] || grayFontColor
                  : ColorsConstants.primaryGray30,
              }}
            />
            <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
              {lastWebhookEvent
                ? `${lastWebhookEvent.event_type} · ${dayjs(lastWebhookEvent.received_at).fromNow()}`
                : "No events received yet"}
            </Typography>
          </Stack>
        </Box>

        <Divider />

        <StringField
          fieldKey="payment_status_variable"
          schema={{ description: "Downstream nodes branch on this variable name" }}
          value={paymentStatusVariable}
          required={false}
          onChange={(val) => onStripeConfigChange("payment_status_variable", val)}
        />

        <Box>
          <Typography sx={fieldLabelSx}>Possible values</Typography>
          <Stack direction="row" gap={1} sx={{ mt: 0.5 }}>
            {PAYMENT_STATUS_VALUES.map((value) => (
              <Chip
                key={value}
                label={value}
                size="small"
                sx={{ backgroundColor: ColorsConstants.secondaryGray, color: grayFontColor }}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const fieldLabelSx = {
  ...typography.bodySmall,
  fontWeight: 600,
  textTransform: "uppercase",
  color: grayFontColor,
  mb: 0.5,
};

export default PaymentLinkStripeSettings;
